import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import { GUARANTEED_ARRIVAL_DAYS } from 'src/utils/guaranteedArrivalDays';

export const selectFacetByKey = (state: ReduxApplicationState, uid: string): Facet | null => {
  return state.productsQuery.facets.find((facet) => facet.key === uid) || null;
};

export const selectTotalActiveOptions = (state: ReduxApplicationState) => {
  return state.productsQuery.facets.reduce((total, facet) => {
    const activeOptionsCount = facet.options.filter((option) => option.active).length;

    total = total + activeOptionsCount;

    return total;
  }, 0);
};

export const selectActiveFacets = (state: ReduxApplicationState) => {
  return state.productsQuery.facets.filter((facet) =>
    facet.options.some((option) => option.active),
  );
};

/**
 * Active facets that are NOT taxonomy levels. Taxonomy selections come from the
 * taxonomy navigation, not the filter sidebar, so they must not be surfaced as
 * "applied filters" or counted in filter badges.
 */
export const selectActiveNonTaxonomyFacets = (state: ReduxApplicationState) => {
  const taxonomyAliases = state.productsQuery.taxonomyAttributeAliases ?? [];

  return selectActiveFacets(state).filter((facet) => !taxonomyAliases.includes(facet.alias));
};

/**
 * Count of active options across non-taxonomy facets, plus a guaranteed arrival day, which
 * is applied like a filter although no facet holds it (for filter-count badges).
 */
export const selectTotalActiveNonTaxonomyOptions = (state: ReduxApplicationState) => {
  return selectActiveNonTaxonomyFacets(state).reduce(
    (total, facet) => total + facet.options.filter((option) => option.active).length,
    state.productsQuery.guaranteedArrivalDay ? 1 : 0,
  );
};

export const selectActiveSortOption = (state: ReduxApplicationState) => {
  return state.productsQuery.sortOptions.find((option) => option.active) || null;
};

/** The guaranteed arrival day as a shopper reads it, "Saturday", while one is set. */
export const selectGuaranteedArrivalDayName = (state: ReduxApplicationState) => {
  const day = state.productsQuery.guaranteedArrivalDay;

  return day && GUARANTEED_ARRIVAL_DAYS[day].name;
};
